import { utils as ethersUtils } from 'ethers';

import { IVipNextLevel } from 'models/vip/i-vip-user.interface';

export const formatVipDate = (dateStr: string): string => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Compact date (e.g. "Jul 22") \u2014 no year/time, for tight mobile rows. */
export const formatVipDateShort = (dateStr: string): string => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const formatThreshold = (threshold: string): string => {
  const num = parseFloat(threshold);
  if (isNaN(num)) return threshold;
  return numberFormatCompact(num);
};

export const isWeiMetric = (metricKey: string): boolean => {
  return [
    'wallet_token_worth',
    'wallet_rmv_balance',
    'wallet_combined_worth',
    'staked_amount',
    'trade_volume_rmv',
  ].includes(metricKey);
};

export const fromWei = (weiValue: string): number => {
  const num = parseFloat(weiValue);
  if (isNaN(num)) return 0;
  return num / 1e18;
};

export const formatProgressValue = (value: string, metricKey: string): string => {
  const num = isWeiMetric(metricKey) ? fromWei(value) : parseFloat(value);
  if (isNaN(num)) return value;
  return numberFormatCompact(num);
};

export const numberFormatCompact = (num: number): string => {
  // parseFloat trims trailing zeros: 100.0K -> 100K, 5.0M -> 5M, 1.5M stays 1.5M.
  const trim = (n: number, suffix: string) => `${parseFloat(n.toFixed(1))}${suffix}`;
  if (num >= 1_000_000_000) return trim(num / 1_000_000_000, 'B');
  if (num >= 1_000_000) return trim(num / 1_000_000, 'M');
  if (num >= 1_000) return trim(num / 1_000, 'K');
  return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

export const RMV_DECIMALS = 18;

export const toWei = (amount: number): string => {
  // Build the wei value with BigNumber, not `amount * 10 ** RMV_DECIMALS`:
  // for amount >= 1000 that product is >= 1e21, which Number.toString() emits in
  // exponential form ("2.5e+22") that ethers BigNumber rejects — and the float
  // multiply also loses precision past 2^53. parseUnits returns exact plain digits.
  // toFixed(18) keeps the fractional part (Math.floor silently dropped it — toWei(0.5)="0"),
  // renders plain-decimal (non-exponential) for all realistic amounts, and caps at 18 fraction
  // digits so parseUnits never overflows on float precision.
  return ethersUtils.parseUnits(amount.toFixed(RMV_DECIMALS), RMV_DECIMALS).toString();
};

export const truncateAddress = (address: string, startLen = 14, endLen = 10): string => {
  if (address.length <= startLen + endLen) return address;
  return `${address.substring(0, startLen)}...${address.substring(address.length - endLen)}`;
};

export const METRIC_UNITS: Record<string, string> = {
  vip_points: 'VIP Points',
  wallet_token_worth: 'wallet token worth',
  wallet_rmv_balance: 'RMV balance',
  wallet_nft_count: 'NFTs held',
  wallet_combined_worth: 'combined wallet worth',
  staked_amount: 'RMV staked',
  trade_volume_rmv: 'trading volume',
  trade_count: 'NFTs traded',
  fraction_count: 'amount of NFT traded',
  asset_nft_id_filter_count: 'specific NFTs held',
  referral_count: 'referrals',
  referral_eligible_count: 'eligible referrals',
  referral_purchases: 'referral purchases',
};

export const getMetricUnit = (metricKey: string, metricLabel?: string): string => {
  return METRIC_UNITS[metricKey] ?? metricLabel ?? metricKey;
};

/**
 * Short units for compact readouts like the hero's "87.1K / 100K RMV" progress line,
 * where `METRIC_UNITS`' descriptive phrasing ("combined wallet worth") is too long.
 * Wei-denominated metrics all read as RMV, so they're handled by family rather than
 * enumerated here.
 */
const METRIC_SHORT_UNITS: Record<string, string> = {
  vip_points: 'pts',
  wallet_nft_count: 'NFTs',
  asset_nft_id_filter_count: 'NFTs',
  fraction_count: 'NFTs',
  trade_count: 'trades',
  spin_count: 'spins',
  referral_count: 'referrals',
  referral_eligible_count: 'referrals',
  referral_purchases: 'purchases',
};

export const getMetricShortUnit = (metricKey: string, metricLabel?: string): string => {
  if (isWeiMetric(metricKey)) return 'RMV';
  return METRIC_SHORT_UNITS[metricKey] ?? metricLabel ?? metricKey;
};

export const getNextLevelReq = (
  nextLevel: IVipNextLevel | null,
): { threshold: string; description: string; userProgress: string; metricKey: string; metricLabel?: string } | undefined => {
  if (!nextLevel || !nextLevel.requirements.length) return undefined;
  const req = nextLevel.requirements[0];
  return {
    threshold: req.threshold,
    description: req.description,
    userProgress: req.user_progress,
    metricKey: req.metric_key,
    metricLabel: req.metric_label,
  };
};

export const HISTORY_PAGE_SIZE = 10;
export const PURCHASE_PAGE_SIZE = 10;

/** Top of the VIP ladder. Mirrors the backend's `MAX_VIP_LEVEL` in `vip/perks.py`. */
export const MAX_VIP_LEVEL = 20;
